import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, ElementRef, HostListener } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { concat, Subscription, of, Observable, tap, Subject, throwError, EMPTY, forkJoin } from 'rxjs';
import { map, switchMap, filter, finalize, catchError, retry, timeout, takeUntil } from 'rxjs/operators';
import { Router, ActivatedRoute, ParamMap, NavigationEnd } from '@angular/router';
import { Title } from "@angular/platform-browser";

import { KeycloakService } from 'keycloak-angular';

import { environment } from 'src/environments/environment';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data';
import { ComponentCanDeactivate } from 'src/app/shared/pending-changes.guard';
import { DocumentApiService } from 'src/app/services/document-api.service';
import { DocumentPreflightApiService } from 'src/app/services/document-preflight-api.service';

import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service';
import { DocumentStackService } from 'src/app/services/document-stack.service';
import { SecurityGroups } from 'src/app/shared/securitygroups';
import { Document, DocItems, DocumentExport, ImportItemFields } from 'src/app/shared/document';
import { DocTypes } from 'src/app/shared/doc-list-entry';
import { ExportPreflightFlow } from 'src/app/shared/export-preflight-flow';
import { ExportGate, PreflightState, exportGate, mergeDocFlag, preflightContribution, preflightIsFresh, preflightSignature } from 'src/app/shared/document-preflight';

/**
 * @description
 * Parent container for the full document editing lifecycle. Orchestrates loading,
 * saving, deleting, exporting, and in-place editing of a single `Document` record.
 *
 * ### Key collaborators
 * | Service                    | Role                                              |
 * |----------------------------|---------------------------------------------------|
 * | `DocumentApiService`       | CRUD for document header, name, type, and items   |
 * | `GetglobalsettingsService` | Global UI settings loaded on init                 |
 * | `DocumentStackService`     | Source of a pre-built `Document` for new-doc mode |
 * | `KeycloakService`          | Role-based visibility guards                      |
 * | `MessageService`           | PrimeNG toast notifications                       |
 * | `Title`                    | Browser tab title updates                         |
 * | `Router` / `ActivatedRoute`| URL-param-driven document loading and navigation  |
 *
 * ### Observable data-flow
 * `ngOnInit` subscribes to `route.paramMap` via `_initialQuery`.
 * On each param change it calls `loadDocumentContentObservable()`, which:
 * 1. Fetches the document via `getDocumentById()`.
 * 2. Runs a parallel `forkJoin` for `getDocumentNameById()` and `getDocTypeById()`.
 * 3. In a final `tap`, snapshots `documentCopy = { ...document }` for change-detection.
 * All long-lived subscriptions use `takeUntil(destroy$)`.
 *
 * ### State flags
 * | Flag                       | Meaning                                           |
 * |----------------------------|---------------------------------------------------|
 * | `contentLoading`           | True while the document or content is being fetched |
 * | `editing`                  | True while a child content row is being edited    |
 * | `noChangesThere`           | True when `document` === `documentCopy` (header)  |
 * | `documentIsReadyForActions`| True when not loading, not editing, no changes    |
 *
 * ### Subscription management
 * - `_initialQuery` — long-lived `paramMap` pipe; torn down via `takeUntil(destroy$)`.
 * - `_documentSaveQuery` / `_documentPostQuery` — re-assigned on each save; previous
 *   subscription cancelled by `takeUntil(destroy$)`.
 * - `_deleteDocQuery` / `_childDocQuery` — fire-and-forget; use `takeUntil(destroy$)`.
 * - `router.events` — filtered to `NavigationEnd`; torn down via `takeUntil(destroy$)`.
 *
 * ### Error handling
 * All API errors flow through `handleError(operation)`, which increments `errorCount`,
 * shows a user-friendly PrimeNG toast, and returns `EMPTY`. `canProceed()` acts as a
 * circuit-breaker: when `errorCount >= MAX_ERRORS` it shows a sticky fatal toast and
 * blocks further action.
 */
@Component({
  selector: 'app-edit-document',
  templateUrl: './edit-document.component.html',
  styleUrls: ['./edit-document.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class EditDocumentComponent implements OnInit, OnDestroy, OnChanges, ComponentCanDeactivate {

  production: boolean = environment.production;

  globalSettinsData: GlobalSettingsData = null;

  /*private*/ _docId: number = 0;
  /*private*/ _docType: number = 1;

  DOCDATE: Date;
  DOCEXPDATE: Date;
  DOCVALDATE: Date;

  private _initialQuery: Subscription;

  private _deleteDocQuery: Subscription;
  private _childDocQuery: Subscription;

  file: any[] = [];

  document: Document = null;
  documentCopy: Document = null;

  docTypeInfo: DocTypes = null;

  displayDC: boolean = false;

  displayND: boolean = false;

  displayMD: boolean = false;

  confirmDeleteDocumentOn: boolean = false;

  confirmDeleteDocumentText: string;
  confirmExportDocumentText: string;

  documentSecurityGroup: SecurityGroups;

  showTabRowInfo: boolean = false;
  showTabDocTypeInfo: boolean = false;
  showTabDetails: boolean = true;
  showTabSeller: boolean = true;
  showTabCustomer: boolean = true;
  showTabConditions: boolean = true;
  showTabPayments: boolean = false;
  showTabErrors: boolean = false;
  showTabTable: boolean = true;
  showTabTransaction: boolean = false;
  showTabSaleResult: boolean = false;

  movementIsReady: boolean = true;

  documentContentStatus: number = 0;  // 0 - Everything is ok, below 10 - minor notes, below 100 - Serious errors
  // Stored in DOCFLAG field

  // Invoice pre-flight check (IPF-3). Held in component state only: after the document is closed and reopened the
  // editor recomputes DOCFLAG from the row checks alone and this contribution is gone until the check is run again.
  preflightState: PreflightState | null = null;
  preflightLoading: boolean = false;
  preflightError: string | null = null;
  preflightDlg: boolean = false;

  // "На печать" gate (IPF-5): the existing typed confirmation, extended with the pre-flight findings. One dialog.
  /** The asynchronous part of the gate: runs the check, continues only if the invoice on screen is still the checked one. */
  private exportFlow = new ExportPreflightFlow({
    context: () => this.document ? {
      docId: this.document.DOCID,
      signature: this.preflightSignatureNow,
      busy: this.editing || this.contentLoading,
      headerClean: this.headerEqual(['DOCFLAG']),
      rowsHighlighted: (this.document.items || []).some(di => di.hlt),
    } : null,
    request: () => this.requestPreflight().pipe(map(state => state.result.maxSeverity)),
    decide: (rowsHighlighted, severity) => this.decideExport(rowsHighlighted, severity),
    warnFailed: () => this.messageService.add({
      severity: 'warn', summary: 'Перевірка наявності',
      detail: 'Не вдалося виконати перевірку наявності товару, друк продовжено без неї.', life: 8000
    }),
    warnChanged: () => this.messageService.add({
      severity: 'info', summary: 'Перевірка наявності',
      detail: 'Документ змінено під час перевірки. Натисніть «На печать» ще раз.', life: 8000
    }),
  });
  /** True while the export-time check runs. */
  public get exportChecking(): boolean { return this.exportFlow.pending; }
  exportConfirmTyped: boolean = true;
  exportConfirmHeader: string = '';
  exportConfirmMessage: string = '';
  exportConfirmSource: ExportGate['source'] = 'rows';

  result: any[] = [];

  resultIsReady: boolean = false;

  private _documentSaveQuery: Subscription;
  private _documentPostQuery: Subscription;

  docHeaderStatement: string = '';
  docFooterStatement: string = '';

  readonly side1Label         = 'Продавец';
  readonly side2Label         = 'Покупатель';

  readonly company1Label      = 'Основная организация продавца:';
  readonly person1Label       = 'Контактное лицо:';
  readonly department1Label   = 'Основной склад:';
  readonly lcoLabel           = 'Юридическое лицо продавца:';
  readonly lcoPer1Label       = 'Ответственный представитель:';
  readonly lcoDep1Label       = 'Подразделение продавца:';

  readonly company2Label      = 'Основная организация покупателя:';
  readonly person2Label       = 'Контактное лицо:';
  readonly department2Label   = 'Основной склад:';
  readonly pcoLabel           = 'Юридическое лицо покупателя:';
  readonly pcoPer2Label       = 'Ответственный представитель:';
  readonly pcoDep2Label       = 'Подразделение покупателя:';

  readonly conditions1Label   = 'Общие условия поставки:';
  readonly conditions2Label   = 'Срок выполнения (поставки):';
  readonly conditions3Label   = 'Примечание для продавца:';
  readonly conditions4Label   = 'Примечание для покупателя:';

  documentSpecific: DocumentExport = null;

  toolbarOptions: any = [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'code-block'],

    [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'script': 'sub' }, { 'script': 'super' }],      // superscript/subscript
    [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent

    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    [{ 'font': [] }],
    [{ 'align': [] }],

    ['clean']                                         // remove formatting button
  ];

  modules: any = {
    toolbar: this.toolbarOptions
  };

  /**
   * @description
   * Returns `true` when the document header has no unsaved changes.
   * Compares each own property of `document` against `documentCopy` using strict equality.
   * The following computed/summary fields are intentionally skipped as they are derived
   * from line items and do not represent user-editable header state:
   * `items`, `DOCITEMS`, `DOCTOTALITEMS`, `DOCSUMU4ETKA`, `DOCVSUM`, `DOCSUMWOVAT`,
   * `DOCVAT`, `DOCSUM`, `DOCNAMESTR`, `DOCSUMPROFIT`, `DOCUMENTISREADY`.
   * Also returns `true` while `contentLoading` is active to prevent premature save prompts.
   */
  public get noChangesThere(): boolean {
    if (this.contentLoading) return true; // If content is loading, then there are no changes there.

    return this.headerEqual([]);
  }

  /**
   * `true` when every compared header property of `document` equals `documentCopy`, ignoring the summary
   * properties listed in {@link noChangesThere} and the names in `alsoSkip`.
   */
  private headerEqual(alsoSkip: string[]): boolean {
    let equal: boolean = true;
    if (this.document && this.documentCopy) {
      for (const props of Object.keys(this.document)) {
        //console.log('*** EditProductComponent in noChangesThere: props: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])

        // Filter out document summary properties (by fact they do not modify document content)

        if (alsoSkip.includes(props)) { continue; }

        switch (props) {
          case 'items':
          case 'DOCITEMS':
          case 'DOCTOTALITEMS':
          case 'DOCSUMU4ETKA':
          case 'DOCVSUM':
          case 'DOCSUMWOVAT':
          case 'DOCVAT':
          case 'DOCSUM':
          case 'DOCNAMESTR':
          case 'DOCSUMPROFIT':
          case 'DOCUMENTISREADY':
            break;

          default:
            equal = (this.document[props] === this.documentCopy[props]);
            break;
        }
        if (!equal) {
          // console.log('*** EditProductComponent in noChangesThere: not equal: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])
          break;
        }
      }
      //  console.log('*** EditProductComponent in noChangesThere: ' + equal)
      //  this.isDocumentReady$.next({ v: this.formInvalid, e: this.editing, l: this.contentLoading, c: equal, d: '1' });
    }
    return equal;
  }

  /** Inverse of `noChangesThere` — `true` when the header has unsaved edits. */
  public get thereAreChanges(): boolean { return !this.noChangesThere; }

  editing: boolean = false;

  knockKnock: boolean = false;

  contentLoading: boolean = true;

  /** Returns `true` when `document` is null (not yet loaded), blocking form actions. */
  public get formInvalid(): boolean { return (!this.document); }

  // Flag to show that document is ready for printing, exporting, etc.
  // It is like Document.DOCUMENTISREADY, but not DB wide, only to avoid confusing in UI during editing and reloading.
  // It is set to true when document is loaded and all required fields are filled in.
  // No edit state, no content loading.
  //
  // It is used to disable buttons and other actions in UI, like hiding of mark in save buttons, for example.

  /**
   * @description
   * Returns `true` when the document is fully loaded and ready for non-editing actions
   * (export, delete, print). Combines: `!formInvalid && !editing && !contentLoading && noChangesThere`.
   */
  public get documentIsReadyForActions(): boolean { return !this.formInvalid && !this.editing && !this.contentLoading && this.noChangesThere; }

  /** Returns `true` when the document is loaded, not loading, not editing, and has unsaved header changes. */
  public get thereAreChangesToSave(): boolean { return !this.formInvalid && !this.contentLoading && !this.editing && this.thereAreChanges; }

  /** Inverse of `thereAreChangesToSave` — disables the save button when no changes are pending. */
  public get documentIsNotReadyForSave(): boolean { return !this.thereAreChangesToSave; }

  /** Returns `true` when the document cannot be flagged (form invalid, editing, or content loading). */
  public get documentFlagNotValid(): boolean { return this.formInvalid || this.editing || this.contentLoading; }
  /** Inverse of `documentFlagNotValid`. */
  public get documentFlagIsValid(): boolean { return !this.documentFlagNotValid; }

  exportDS: boolean = false;

  exportConfirmDlg: boolean = false;

  displayImportDlg: boolean = false;

  importParamsMap: ImportItemFields[] = [
    { fieldName: 'PRODCODE', fieldLabel: 'Код товара', fieldIsMandatory: true, fieldType: 'str' },
    { fieldName: 'DOCITEMQTY', fieldLabel: 'Количество', fieldIsMandatory: true, fieldType: 'num' },
    { fieldName: 'DOCITEMPRICE', fieldLabel: 'Цена товара', fieldIsMandatory: true, fieldType: 'num' },
    { fieldName: 'U4ETKA', fieldLabel: 'Учетка', fieldIsMandatory: false, fieldType: 'num' },
    { fieldName: 'DOCITEMPRODNAME', fieldLabel: 'Наименование товара', fieldIsMandatory: false, fieldType: 'str' },
    { fieldName: 'DOCITEMVPRICE', fieldLabel: 'Цена в валюте', fieldIsMandatory: false, fieldType: 'num' },
    { fieldName: 'DOCITEMTEXT1', fieldLabel: 'Примечание 1', fieldIsMandatory: false, fieldType: 'str' },
    { fieldName: 'DOCITEMTEXT2', fieldLabel: 'Примечание 2', fieldIsMandatory: false, fieldType: 'str' },
  ];
  /** `true` when the document type is an invoice (DOCTYPE 1 or 12). */
  public get docTypeInvoice(): boolean { return (this._docId) && (this.document) && ([1, 12].includes(this.document.DOCTYPE)); }
  /** `true` when the document type is a payment record (DOCTYPE 8, 9, or 11). */
  public get docTypePayment(): boolean { return (this._docId) && (this.document) && ([8, 9, 11].includes(this.document.DOCTYPE)); }

  /** `true` when the document is a sale invoice (DOCTYPE 1) that contains at least one manufactured item (`PRODKIND == 1`). */
  public get thereIsManufacture(): boolean {
    return (this._docId) &&
      (this.document) &&
      ([1].includes(this.document.DOCTYPE)) &&
      (((this.document.items || []).find(e => e.PRODKIND == 1)) != null)
      ;
  }

  /** `true` when the user has the `portal-admin` or `finance-manager` Keycloak role. */
  public get canSeeFinanceResult(): boolean { return (this.auth.isUserInRole('portal-admin') || this.auth.isUserInRole('finance-manager')); }

  selectedSection: string = 'c'; // ['raw','head','type','sel','buy','cond','table','man','pay','res','tran', ]

  @ViewChild('anchorDashrow') anchorRow: ElementRef;
  @ViewChild('anchorDashhead') anchorHEad: ElementRef;
  @ViewChild('anchorDashtype') anchorTYpe: ElementRef;
  @ViewChild('anchorDashsel') anchorSEl: ElementRef;
  @ViewChild('anchorDashbuy') anchorBuy: ElementRef;
  @ViewChild('anchorDashcond') anchorCond: ElementRef;
  @ViewChild('anchorDasherr') anchorErr: ElementRef;
  @ViewChild('anchorDashtable') anchorTable: ElementRef;
  @ViewChild('anchorDashman') anchorMan: ElementRef;
  @ViewChild('anchorDashpay') anchorPay: ElementRef;
  @ViewChild('anchorDashres') anchorRes: ElementRef;
  @ViewChild('anchorDashtran') anchorTran: ElementRef;

  tabRouter: any[] = [];

  navigationMenu: MenuItem[] = [{
    label: 'Навигация по документу',
    icon: 'pi pi-home',
    command: () => { },
    enabled: true,
    disabled: false,
    items:[]
  }];

  baseTitle: string = 'Редактирование документа';

  swapLeftToRight: boolean = true;

  fullURL: string = window.location.href;
  baseURL: string = window.location.href;
  hostname: string = window.location.hostname;
  origin: string = window.location.origin;

  countEmpty: boolean = false;

  private destroy$ = new Subject<void>();

  errorCount: number = 0;
  readonly MAX_ERRORS: number = 5;

  /**
   * @description
   * Centralized error handler factory. Returns a callback suitable for use in
   * `catchError`. On invocation it:
   * 1. Increments `errorCount`.
   * 2. Logs the error to the console.
   * 3. Displays a user-friendly PrimeNG toast via `MessageService` (5 s lifetime).
   * 4. Returns `of(result)` when a fallback value is provided, otherwise `EMPTY`.
   * @param operation - A string key identifying the failing operation (used for the
   *   friendly-message map and console output).
   * @param result - Optional fallback value emitted by the returned Observable.
   */
  private handleError<T>(operation: string, result?: T) {
    return (error: any): Observable<T> => {
      this.errorCount++;

      console.error(`${operation} failed:`, error);

      // Log to monitoring service (if available)
      // this.errorLoggingService.logError(error);

      // Show user-friendly message
      this.messageService.add({
        severity: 'error',
        summary: 'Помилка операції',
        detail: this.getUserFriendlyError(operation, error),
        life: 5000
      });

      // Stop loading indicators
      //this.contentIsLoading = false;
      //this.contentLoading.emit(false);

      // Return safe fallback
      return result ? of(result) : EMPTY;
    };
  }

  /** Maps `operation` keys to Ukrainian error messages and appends HTTP-status context. */
  private getUserFriendlyError(operation: string, error: any): string {
    const errorMap: { [key: string]: string } = {
      'calculateDocItemsFields': 'Не вдалося завантажити дані документа',
      'saveDocItem': 'Не вдалося зберегти позицію',
      'deleteDocItem': 'Не вдалося видалити позицію',
      'loadProduct': 'Не вдалося завантажити дані товару',
      'calculateProfit': 'Помилка розрахунку прибутку',
      'exportData': 'Помилка експорту даних'
    };

    const message = errorMap[operation] || 'Невідома помилка';

    if (error.status === 0) {
      return `${message}: Немає з'єднання з сервером`;
    }
    if (error.status === 401) {
      return `${message}: Немає авторизації`;
    }
    if (error.status === 403) {
      return `${message}: Немає прав доступу`;
    }
    if (error.status === 404) {
      return `${message}: Дані не знайдено`;
    }
    if (error.status >= 500) {
      return `${message}: Помилка сервера`;
    }

    return message;
  }

  /**
   * @description
   * Circuit-breaker guard. Returns `false` and shows a sticky fatal toast when
   * `errorCount >= MAX_ERRORS` (default 5), signalling that something is seriously
   * wrong and the user should reload the page. Returns `true` otherwise.
   */
  // Check if too many errors occurred
  private canProceed(): boolean {
    if (this.errorCount >= this.MAX_ERRORS) {
      this.messageService.add({
        severity: 'error',
        summary: 'Критична помилка',
        detail: 'Занадто багато помилок. Перезавантажте сторінку.',
        sticky: true
      });
      return false;
    }
    return true;
  }

  private intersectionObserver: IntersectionObserver;
  private currentVisibleSection: string = 'head';
  private elementToRouteMap: Map<Element, any> = new Map();

  constructor(
    private docApi: DocumentApiService,
    private preflightApi: DocumentPreflightApiService,
    private auth: KeycloakService,
    private messageService: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private globalsettingsservice: GetglobalsettingsService,
    private documentStack: DocumentStackService,
    private titleService: Title,
  )
  {
    const [hash, page, dt, did, sec] = window.location.hash.split('/');

    this.baseURL = window.location.protocol + '//' + window.location.hostname + ':' + window.location.port + '/' + hash + '/' + page + '/' + dt + '/' + did + '/';

    this.tabRouter = [
      { label: 'DbRaw', icon: 'pi pi-fw pi-desktop', anchor: () => { return this.anchorRow; }, section: 'row', activateTab: () => { this.showTabRowInfo = true; } },
      { label: 'Реквизиты, дата и сроки', icon: 'pi pi-fw pi-calendar', anchor: () => { return this.anchorHEad; }, section: 'head', activateTab: () => { this.showTabDetails = true; } },
      { label: 'Преамбула и условия', icon: 'pi pi-fw pi-book', anchor: () => { return this.anchorTYpe; }, section: 'type', activateTab: () => { this.showTabDetails = true; } },
      { label: 'Продавец', icon: 'pi pi-fw pi-home', anchor: () => { return this.anchorSEl; }, section: 'sel', activateTab: () => { this.showTabSeller = true; } },
      { label: 'Покупатель', icon: 'pi pi-fw pi-users', anchor: () => { return this.anchorBuy; }, section: 'buy', activateTab: () => { this.showTabCustomer = true; } },
      { label: 'Условия поставки', icon: 'pi pi-fw pi-wallet', anchor: () => { return this.anchorCond; }, section: 'cond', activateTab: () => { this.showTabConditions = true; } },
      { label: 'Замечания в документе', icon: 'pi pi-fw pi-question', anchor: () => { return this.anchorErr; }, section: 'err', activateTab: () => { this.showTabErrors = true; } },
      { label: 'Содержание документа', icon: 'pi pi-fw pi-table', anchor: () => { return this.anchorTable; }, section: 'table', activateTab: () => { this.showTabTable = true; } },
      { label: 'Производство', icon: 'pi pi-fw pi-hammer', anchor: () => { return this.anchorMan; }, section: 'man', activateTab: () => { } },
      { label: 'Оплата', icon: 'pi pi-fw pi-credit-card', anchor: () => { return this.anchorPay; }, section: 'pay', activateTab: () => { this.showTabPayments = true; } },
      { label: 'Результат', icon: 'pi pi-fw pi-chart-bar', anchor: () => { return this.anchorRes; }, section: 'res', activateTab: () => { this.showTabSaleResult = true; } },
      { label: 'Транзакции', icon: 'pi pi-fw pi-envelope', anchor: () => { return this.anchorTran; }, section: 'tran', activateTab: () => { this.showTabTransaction = true; } }
    ];

    this.navigationMenu[0].items = this.tabRouter.map(e => {  
        return {
          label: e.label,
          icon: e.icon,
          command: () => { this.router.navigate([page, this._docType, this._docId, e.section] )},
          enabled: true,
          disabled: false,
          //url: '/',
          //routerLink: [page, this._docId, this._docType, e.section],
        }
    }
    );

    environment.production || console.log('*** EditDocumentComponent constructor: ===> this.navigationMenu: ' + JSON.stringify(this.navigationMenu, null, 2));

    console.log('*** EditDocumentComponent constructor: ===> prepareExternalUrl: ' + this.baseURL);
  }

  /** Guards route deactivation and browser unload; returns `false` (blocks navigation) when unsaved changes are present. */
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return this.documentIsReadyForActions;
  }

  /**
   * @description
   * Builds the `IntersectionObserver` that tracks which document section is currently
   * visible in the viewport and updates the browser title and navigation-menu state
   * accordingly. If the `elementToRouteMap` is empty when called (i.e. the view has
   * not yet rendered all anchor elements), the method schedules itself to retry after
   * 500 ms via `setTimeout`.
   */
  private setupIntersectionObserver(): void {
    console.log('*** EditDocumentComponent in setupIntersectionObserver');

    // Disconnect existing observer if any
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // Clear and rebuild the map
    this.elementToRouteMap.clear();

    // First pass: build the element-to-route mapping
    this.tabRouter.forEach(route => {
      const anchorElement = route.anchor();
      environment.production || console.log('*** EditDocumentComponentChecking route:', route.section, 'anchorElement:', anchorElement);
      if (anchorElement?.nativeElement) {
        this.elementToRouteMap.set(anchorElement.nativeElement, route);
        environment.production || console.log('*** EditDocumentComponent Mapped element for section:', route.section, anchorElement.nativeElement);
      } else {
        console.warn('*** EditDocumentComponent No anchor element found for section:', route.section);
      }
    });

    environment.production || console.log('*** EditDocumentComponent Total mapped elements:', this.elementToRouteMap.size);

    if (this.elementToRouteMap.size === 0) {
      console.warn('*** EditDocumentComponent No elements to observe, will retry in 500ms');
      setTimeout(() => this.setupIntersectionObserver(), 500);
      return;
    }

    const options = {
      root: null,
      rootMargin: '0px 0px -50% 0px',
      threshold: [0, 0.1, 0.5, 1]
    };

    this.intersectionObserver = new IntersectionObserver((entries) => {
      environment.production || console.log('*** EditDocumentComponent IntersectionObserver callback, entries count:', entries.length);

      entries.forEach(entry => {
        environment.production || console.log('*** EditDocumentComponent Entry:', {
          isIntersecting: entry.isIntersecting,
          target: entry.target,
          ratio: entry.intersectionRatio
        });

        if (entry.isIntersecting) {
          const route = this.elementToRouteMap.get(entry.target);
          environment.production || console.log('*** EditDocumentComponent Found route:', route?.section);

          if (route && this.currentVisibleSection !== route.section) {
            environment.production || console.log('*** EditDocumentComponent Updating section from', this.currentVisibleSection, 'to', route.section);
            this.currentVisibleSection = route.section;
            this.updateTitleAndNavigation(route);
          }
        }
      });
    }, options);

    // Second pass: observe all elements
    this.elementToRouteMap.forEach((route, element) => {
      this.intersectionObserver.observe(element);
      environment.production || console.log('*** EditDocumentComponent Observing element for section:', route.section);
    });
  }

  /** Updates the browser title to `baseTitle: docNumber: sectionLabel` and marks the matching nav-menu item as disabled (current section). */
  private updateTitleAndNavigation(route: any): void {
    if (!this.document) return;

    environment.production || console.log('*** EditDocumentComponent section changed to:', route.section);

    this.selectedSection = route.section;
    this.titleService.setTitle(`${this.baseTitle}: ${this.document.DOCNUMBEREX.trim()}: ${route.label}`);

    this.navigationMenu[0].items.forEach(item => {
      item.disabled = (item.label === route.label);
    });
  }

  /** Completes `destroy$`, which tears down all `takeUntil(destroy$)` subscriptions and disconnects the `IntersectionObserver`. */
  ngOnDestroy(): void {
    console.log('*** EditDocumentComponent in ngOnDestroy')

    this.exportFlow.cancel();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Logs simple-changes events (Angular lifecycle hook; no business logic). */
  ngOnChanges(changes: SimpleChanges): void {
    console.log('*** EditDocumentComponent ngOnChanges: ===> ' + JSON.stringify(changes));
  }

  /**
   * @description
   * Returns an `Observable<Document>` that loads the full document for the current
   * `_docId`. Two branches:
   * - **Existing document** (`_docId > 0`): calls `getDocumentById()`, applies a
   *   `timeout(10000)` + `retry(1)`, then runs a parallel `forkJoin` for name and type.
   *   A final `tap` snapshots `documentCopy`.
   * - **New document** (`_docId === 0`): reads the document from `DocumentStackService`
   *   at index 0 and returns it synchronously via `of(document)`.
   */
  loadDocumentContentObservable(): Observable<Document> {
    environment.production || console.log('*** EditDocumentComponent loadDocumentContent: ===> Started');

    if (this._docId) {
      console.log('*** EditDocumentComponent loadDocumentContent docid: ' + this._docId);

      return this.docApi.getDocumentById(this._docId).pipe(
        filter(doc => doc?.length > 0),
        timeout(10000),
        retry(1),
        catchError(this.handleError('getDocumentById', [this.documentStack.getDocument(0)])), // Fallback to empty document on error

        map(doc => {
          this.document = doc[0];
          this.document.DOCUMENTISREADY = false;
          this.DOCDATE = new Date(this.document.DOCDATE);
          this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
          this.DOCVALDATE = new Date(this.document.DOCVALDATE);
          this._docType = this.document.DOCTYPE;

          console.log('*** EditDocumentComponent in map loadDocumentContent: ===> ' + this.DOCDATE);

          return this.document;
        }),

        switchMap(doc =>
          forkJoin({
            name: this.docApi.getDocumentNameById(doc.DOCID).pipe(
              timeout(10000),
              retry(1),
              catchError(this.handleError('getDocumentNameById', [{ DOCNAMESTR : ''}])),
              map(data => {
                const docName = data[0];
                environment.production || console.log('*** EditDocumentComponent loadDocumentName: ===> doc 0: ' + JSON.stringify(docName));
                return docName;
              })
            ),

            type: this.docApi.getDocTypeById(doc.DOCTYPE).pipe(
              timeout(10000),
              retry(1),
              catchError(this.handleError('getDocTypeById', null)),
              map(data => {
                environment.production || console.log('*** EditDocumentComponent in get DOCTYPE: ===> ' + JSON.stringify(data));
                return data[0];
              })
            )
          }).pipe(
            map(({ name, type }) => {
              if (name && 'DOCNAMESTR' in name) {
                this.document.DOCNAMESTR = name.DOCNAMESTR;
                this.titleService.setTitle(this.document.DOCNAMESTR);
              }

              if (type) {
                this.docTypeInfo = type as DocTypes;
                this.document.HeaderStatement = this.docTypeInfo.DOCTEXTBEFORE;
                this.document.FooterStatement = this.docTypeInfo.DOCTEXTAFTER;
              }
              return this.document;
            })
          )
        ),

        tap(() => {
          this.documentCopy = { ...this.document };
          console.log('*** EditDocumentComponent loadDocumentContent: ===> Ended');
        })
      );
    } else {
      /*
       * (this._docId == 0) New Document Mode
       *
       * get Document from Document Stack
       */
      let newIndex = 0;

      this.document = this.documentStack.getDocument(newIndex);
      if (this.document) {
        this.DOCDATE = new Date(this.document.DOCDATE);
        this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
        this.DOCVALDATE = new Date(this.document.DOCVALDATE);
      }

      console.log('*** EditDocumentComponent loadDocumentContent: ===> Ended (new document)');
      return of(this.document);
    }
  }

  /** Activates the tab for the given section code and smooth-scrolls to its anchor element after a 200 ms delay. */
  routeByParameters(selectedSection: string) {
    environment.production || console.log('*** EditDocumentComponent  in routeByParameters: tabRouter: ' + JSON.stringify(this.tabRouter));
    console.log('*** EditDocumentComponent in routeByParameters: selectedSection: ' + selectedSection);

    let route = this.tabRouter.find(s => s.section == selectedSection);
    environment.production || console.log('*** EditDocumentComponent  in routeByParameters: found section: ' + JSON.stringify(route));
    if (route) {
      environment.production || console.log('*** EditDocumentComponent in routeByParameters: route: ' + JSON.stringify(route));

      route.activateTab();

      // Scroll to the anchor element after a short delay to ensure the view is ready
      setTimeout(() => {
        if (route.anchor()) {
          route.anchor().nativeElement.scrollIntoView({ behavior: 'smooth' });
          this.selectedSection = selectedSection;
        }
      }, 200);
    }
  }

  /**
   * @description
   * Initialises the component. Reads global settings, attaches a `NavigationEnd` listener
   * (auto-torn down via `takeUntil(destroy$)`), and starts the main `paramMap` →
   * `switchMap` → `loadDocumentContentObservable()` chain that re-runs the full
   * document load on every route-parameter change. Sets `contentLoading = false` after
   * each successful emission and calls `setupIntersectionObserver()` in `finalize`.
   */
  ngOnInit(): void {
    console.log('*** EditDocumentComponent in ngOnInit');
    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this.router.events.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((event: NavigationEnd) => {
      console.log('*** EditDocumentComponent in NavigationEnd: ' + this.baseURL);
    });

    this._initialQuery =
      this.route.paramMap.pipe(
        takeUntil(this.destroy$),

        switchMap((params: ParamMap) => {

          this._docId = +params.get('id');
          this._docType = +params.get('type');

          // Another document is about to be loaded into this component: a pending export-time check and the
          // confirmation it may have opened described the previous one.
          this.exportFlow.cancel();
          this.exportConfirmDlg = false;

          return this.loadDocumentContentObservable().pipe(
            tap(() => {
              if (params.get('section')) {
                this.routeByParameters(params.get('section'));
              }
            }),

            catchError(this.handleError('loadDocumentContentObservable', [])),

            finalize(() => {
              console.log('*** EditDocumentComponent loadDocumentContentObservable finalized');
              this.setupIntersectionObserver();
            })
          );
        }),

        tap(() => {
          this.contentLoading = false;
        }),

        catchError(this.handleError('initialDocumentLoading', [null])),

        finalize(() => {
          console.log('*** EditDocumentComponent ngOnInit finalized');
        })

    ).subscribe({
      error: (err) => {
        console.error('*** EditDocumentComponent ngOnInit error:', err);
        this.canProceed();
      },
      complete: () => {
        console.log('*** EditDocumentComponent ngOnInit completed');
      }
    });
  }
  /** Updates `DOCDATE` and auto-computes `DOCVALDATE` / `DOCEXPDATE` from the doc-type's default terms (DOCTYPE 1: +7/+30 days; DOCTYPE 12: +14/+60 days). */
  onDateSelect(event) {
    console.log('*** EditDocumentComponent in onDateSelect: ' + JSON.stringify(event));
    let dddd = new Date(event);
    this.document.DOCDATE = dddd;

    switch (this.document.DOCTYPE) {
      case 1:
        this.document.DOCVALDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTVALTERM || 7))
        this.document.DOCEXPDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTEXPTERM || 30))
        break

      case 12:
        this.document.DOCVALDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTVALTERM || 14))
        this.document.DOCEXPDATE = new Date(dddd.getFullYear(), dddd.getMonth(), dddd.getDate() + (this.docTypeInfo.DEFAULTEXPTERM || 60))
        break;
    }

    this.DOCEXPDATE = new Date(this.document.DOCEXPDATE);
    this.DOCVALDATE = new Date(this.document.DOCVALDATE);
  }

  /** Sets `DOCEXPDATE` on the document when the expiry date picker fires. */
  onDateExpSelect(event) {
    console.log('*** EditDocumentComponent in onDateSelect: ' + JSON.stringify(event));
    this.document.DOCEXPDATE = event;
  }

  /** Sets `DOCVALDATE` on the document when the validity date picker fires. */
  onDateValSelect(event) {
    console.log('*** EditDocumentComponent in onDateSelect: ' + JSON.stringify(event));
    this.document.DOCVALDATE = event;
  }

  /** Updates `DOCCURRID` and `DOCRATEVAL` from the selected currency object and triggers a shallow document clone to propagate to child components. */
  onCurrencySelection(event) {
    console.log('*** EditDocumentComponent in onCurrencySelection: ' + JSON.stringify(event));
    if (event && ('CURID' in event)) {
      //if (this.document.DOCCURRID != event.CURID) {
      //}
      this.document.DOCCURRID = event.CURID;
      this.document.DOCRATEVAL = event.CURRATE;

      this.document = { ... this.document };
    }
  }

  /** Handles selection in the recent-documents panel (logging stub). */
  onRecentRowSelect(selectedId) {
    console.log('*** EditDocumentComponent in onRecentRowSelect: ' + selectedId);
  }

  /** Logs which accordion tab was opened (stub). */
  onTabOpen(event) { console.log('*** EditDocumentComponent in onTabOpen: ' + event.index); }

  /** Sets `document.PRIMDOCID` to the selected parent's `DOCID`, or `null` if the selection is cleared. */
  parentDocumentSelected(event) {
    console.log('*** EditDocumentComponent in parentDocumentSelected: ' + JSON.stringify(event));
    this.document.PRIMDOCID = (event) ? event.DOCID : null;
  }

  /** Applies the chosen security group to `documentSecurityGroup` and writes its `SECGROUPID` to `document.SGMGROUPID`. */
  onSelectedSecurityGroup(sg: SecurityGroups) {
    console.log('*** EditDocumentComponent in onSelectedSecurityGroup: ' + JSON.stringify(sg));
    this.documentSecurityGroup = sg;
    this.document.SGMGROUPID = sg.SECGROUPID;
  }

  /**
   * @description
   * Handles the save confirmation triggered by the child toolbar. Branches on `_docId`:
   * - **PUT** (`_docId > 0`): runs a sequential `concat` of `putDocumentById()` followed
   *   by `getDocumentNameById()` to refresh the page title. Errors flow through
   *   `handleError('saveDocument')`.
   * - **POST** (`_docId === 0`): calls `postDocumentById()`, captures the new `DOCID`,
   *   then navigates to `/edit-document/<DOCID>`. Errors flow through
   *   `handleError('saveDocument')`.
   * Both paths use `takeUntil(destroy$)`.
   * @param e - The confirmation event value (reserved for future multi-section saves).
   */
  onConfirm(e: number) {
    console.log('*** EditDocumentComponent onConfirm: ' + e)

    switch (e) {
      default:
        console.log('*** EditDocumentComponent onConfirm: (Main data) ');
        environment.production || console.log('*** EditDocumentComponent buttonSaveAllClick: document ' + JSON.stringify(this.document));
        environment.production || console.log('*** EditDocumentComponent buttonSaveAllClick: documentCopy ' + JSON.stringify(this.documentCopy));

        // A header-only edit (delivery date, company) does not recalculate the row status, so the pre-flight
        // contribution is re-evaluated here, and a stale result never reaches the saved DOCFLAG.
        this.applyDocFlag();

        if (this._docId) {
          this._documentSaveQuery =
            concat(
              this.docApi.putDocumentById(this.document)
                .pipe(map(data => {
                  this.documentCopy = { ... this.document };
                  this.document = { ... this.document };        // Trigger the changes in child components

                  console.log('*** EditDocumentComponent onConfirm: *** PUT *** ');
                })),
              this.docApi.getDocumentNameById(this._docId)
                .pipe(map(data => {
                  let docName: any = data[0];

                  if (docName) {
                    this.document.DOCNAMESTR = docName.DOCNAMESTR;
                    this.titleService.setTitle(this.document.DOCNAMESTR);
                  }
                }))
            )
            .pipe(
              catchError(this.handleError('saveDocument')),
              takeUntil(this.destroy$)
            )
            .subscribe();
        } else {
          this._documentPostQuery =
            this.docApi.postDocumentById(this.document)
              .pipe(
                map(data => {
                  console.log('*** EditDocumentComponent onConfirm: *** on Post data map *** ' + JSON.stringify(data));
                  this._docId = data['DOCID'];
                }),
                catchError(this.handleError('saveDocument')),
                takeUntil(this.destroy$)
              )
            .subscribe(() => {
              this.router.navigate(['/edit-document', this._docId]);
              console.log('*** EditDocumentComponent onConfirm: *** POST *** ');
              });
        }
        break;
    }
  }

  /**
   * @description
   * Activates a single document section while hiding all others. Accepts one of three
   * values for `section`:
   * - `'conditions'` — shows `showTabConditions`; hides all others.
   * - `'content'`    — shows `showTabTable`; hides all others.
   * - `'transactions'` — shows `showTabTransaction`; hides all others.
   * @param section - The section to make visible.
   */
  showSection(section: 'conditions' | 'content' | 'transactions'): void {
    console.log('*** EditDocumentComponent in showSection: ' + section);
    this.showTabRowInfo       = false;
    this.showTabDetails       = false;
    this.showTabSeller        = false;
    this.showTabCustomer      = false;
    this.showTabConditions    = section === 'conditions';
    this.showTabTable         = section === 'content';
    this.showTabTransaction   = section === 'transactions';
    this.showTabPayments      = false;
    this.showTabSaleResult    = false;
  }

  /** Handles new-document creation events from the child toolbar (stub — overlay logic not yet implemented). */
  onNewDocument(event) {
    console.log('*** EditDocumentComponent onNewDocument: ' + JSON.stringify(event))
  }

  /** Triggers a shallow document clone to propagate the refilled content to child components. */
  onRefillDocument(event) {
    console.log('*** EditDocumentComponent onRefillDocument: ' + JSON.stringify(event))
    if (event) {
      this.document = { ...this.document }; // Trigger reload
    //  environment.production || console.table(this.document);
    //  environment.production || console.table(this.document.items);
    }
  }

  /**
   * @description
   * Checks all document line items for the `hlt` (highlighted / error) flag synchronously.
   * If no items are highlighted, or if the app is not in production mode, sets
   * `exportDS = true` to open the export overlay directly.
   * Otherwise sets `confirmExportDocumentText` and `exportConfirmDlg = true` to show
   * a confirmation dialog before proceeding.
   */
  onExportDocument(event) {
    console.log('*** EditDocumentComponent onExportDocument: ' + JSON.stringify(event))
    //environment.production || console.table(this.document);
    environment.production || console.table(this.document.items);

    this.documentSpecific = {

        Owner: {
          FullNameLabel: 'Відповідальний',
          EmailLabel: 'Електронна пошта',
          PhoneLabel: 'Контактний телефон',
        },

        Header: {
          DateLabel: 'Дата',
          DocumentNameLabel: 'Назва',
          DocumentNumberLabel: 'Номер',
          ValidToLabel: 'Дійсний до',
          ExpiredOnLabel: 'Дата виконання',

          CurrencyNameLabel: 'Валюта документа',
          CurrencyRateLabel: 'Курс валюти',
          MainCurrencyNameLabel: 'Валюта документа',

        },

        Seller: {
          CompanyNameLabel: 'ПРОДАВЕЦЬ',
          CompanyIdLabel: 'Код ЄДРПОУ',
          VATIDLabel: 'Код платника ПДВ',
          VATIDDocLabel: 'Свідоцтво ПДВ',
          AccountLabel: 'Рахунок',
          BankLabel: 'Банк',
          AddressLabel: 'Адреса',
          FullNameLabel: 'Відповідальний',
          EmailLabel: 'Електронна пошта',
          PhoneLabel: 'Телефон',
        },

        Buyer: {
          CompanyNameLabel: 'ПОКУПЕЦЬ',
          CompanyIdLabel: 'Код ЄДРПОУ',
          VATIDLabel: 'Код платника ПДВ',
          VATIDDocLabel: 'Свідоцтво ПДВ',
          AccountLabel: 'Рахунок',
          BankLabel: 'Банк',
          AddressLabel: 'Адреса',
          FullNameLabel: 'Відповідальний',
          EmailLabel: 'Електронна пошта',
          PhoneLabel: 'Телефон',
        },

        ItemsLabel: {
        ItemCodeLabel: 'Код товару',
        ItemNameLabel: 'Найменування товару',
        ItemUrlLabel: 'Посилання', 
        ItemTariffCodeLabel: 'Код УКТЗЕД',
        ItemNoteLabel: 'Примітки',
        ItemPriceLabel: 'Ціна з ПДВ',
        ItemVPriceLabel: 'Ціна в валюті',
        ItemSumWoVATLabel: 'Сума без ПДВ',
        ItemPriceWoVATLabel: 'Ціна без ПДВ',
        ItemRetailPriceLabel: 'Роздріб',
        ItemRetailVPriceLabel: 'Роздріб в валюті',
        ItemDiscountLabel: 'Знижка',
        ItemQntyLabel: 'Кількість',
        ItemSumLabel: 'Сума з ПДВ',
        ItemVSumLabel: 'Сума в валюті',
        ItemVATLabel: 'ПДВ',

        ItemVendorPriceLabel: 'ItemVendorPriceLabel',
        ItemInvoicePriceLabel: 'ItemInvoicePriceLabel',
        ItemInvoiceSumLabel: 'ItemInvoiceSumLabel',

        ItemIncomePriceLabel: 'ItemIncomePriceLabel',
        ItemIncomeSumLabel: 'ItemIncomeSumLabel',

        ItemProdWeightLabel: 'ItemProdWeightLabel',
        ItemProdWeightSumLabel: 'ItemProdWeightSumLabel',
      },

      Conditions: {
        DeliveryConditionsLabel: 'Умови постачання',
        DeliveryTermsLabel: 'Очікуванний термін виконаня (днів)',
        DocumentNoteLabel: 'Примітки документа',
      },

      Summary: {
        TotalSumLabel: 'Загалом, з ПДВ',
        TotalVSumLabel: 'Загалом, з ПДВ, в валюті',
        TotalSumWithOutVATLabel: 'Сума без ПДВ',
        TotalSumVATLabel: 'Сума ПДВ',
        TotalItemsLabel: 'Усього найменувань',
        TotalQuantityLabel: 'Загальна кількість',
        TotalByWordsLabel: 'Усього прописом',
      }
    };

    const rowsHighlighted = this.document.items.some(di => di.hlt);

    // Today's check runs in production only; the pre-flight joins it and is skipped with it.
    if (!environment.production) {
      this.exportDS = true;
      return;
    }

    // Pre-flight (invoices only, Q1): use the held result while it is fresh, otherwise run the check first (Q10).
    // The answer is used only if the invoice on screen is still the checked one (see ExportPreflightFlow); the row
    // flags are read again when it arrives.
    if (this.preflightApplicable && !this.preflightFresh && this.preflightCanRun) {
      this.exportFlow.start();
      return;
    }

    this.decideExport(rowsHighlighted, this.preflightFresh ? this.preflightState.result.maxSeverity : 0);
  }

  /**
   * Opens the print dialog, or the one confirmation dialog when the row checks or the pre-flight result raise something:
   * typed document number for highlighted rows or a serious finding, a plain Continue for a minor one.
   * The wording for highlighted rows alone is the legacy text, unchanged.
   */
  private decideExport(rowsHighlighted: boolean, preflightSeverity: number): void {
    const gate = exportGate(rowsHighlighted, preflightSeverity);
    if (!gate.confirm) {
      this.exportDS = true;
      return;
    }
    this.exportConfirmTyped = gate.typed;
    this.exportConfirmSource = gate.source;
    switch (gate.source) {
      case 'rows':
        this.exportConfirmHeader = 'В документе есть замечания!';
        this.exportConfirmMessage = 'В документе есть замечания! Их необходимо устранить. Если вы считаете что все Ок, наберите кодовую фразу...';
        break;
      case 'both':
        this.exportConfirmHeader = 'В документі є зауваження';
        this.exportConfirmMessage = 'У документі є позначені рядки та зауваження щодо наявності товару. Якщо ви вважаєте, що все гаразд, наберіть кодову фразу...';
        break;
      default:
        if (gate.typed) {
          this.exportConfirmHeader = 'Перевірка наявності: серйозні зауваження';
          this.exportConfirmMessage = 'Товару може не вистачити для цього документа. Якщо ви вважаєте, що все гаразд, наберіть кодову фразу...';
        } else {
          this.exportConfirmHeader = 'Перевірка наявності: є зауваження';
          this.exportConfirmMessage = 'Є незначні зауваження щодо наявності товару. Продовжити друк?';
        }
        break;
    }
    this.confirmExportDocumentText = this.document.DOCNUMBER;
    this.exportConfirmDlg = true;
  }

  /** Sets `exportDS = true` to open the export overlay after the user confirms export despite highlighted line-item warnings. */
  onExportDocumentConfirmation(event) {
    console.log('*** EditDocumentComponent onExportDocumentConfirmation: ' /*+ JSON.stringify(event)*/);

    this.exportDS = true;
  }

  /**
   * @description
   * Executes the confirmed document deletion. Calls `deleteDocumentById()` and on
   * success navigates to the `/documents` list. Any API error is forwarded to
   * `handleError('deleteDocument')` for toast display. Uses `takeUntil(destroy$)`.
   */
  onDeleteDocumentConfirmation(event) {
    console.log('*** EditDocumentComponent onDeleteDocumentConfirmation: '/* + JSON.stringify(event)*/);

    this._deleteDocQuery =
      this.docApi.deleteDocumentById(this.document.DOCID)
        .pipe(
          catchError(this.handleError('deleteDocument')),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          console.log('*** EditDocumentComponent onDeleteDocumentConfirmation: DONE!');
          this.router.navigate(['/documents']);
        });
  }

  /**
   * @description
   * Guards the delete action by first fetching the document's child list via
   * `getChildDocList()`. If the list is empty, sets `confirmDeleteDocumentOn = true` to
   * show the delete confirmation dialog. If children exist, shows a PrimeNG error toast
   * explaining deletion is impossible. Uses `takeUntil(destroy$)`.
   */
  deleteDocumentClick(event) {
    console.log('*** EditDocumentComponent deleteDocumentClick: ' /*+ JSON.stringify(event)*/);

    this._childDocQuery =
      this.docApi.getChildDocList(this._docId).pipe(
        map(doc => {
          if (doc.length == 0) {
            this.confirmDeleteDocumentText = 'DELETE ' + this.document.DOCNUMBER;
            this.confirmDeleteDocumentOn = true;
          } else {
            this.messageService.add({ severity: 'error', summary: 'Этот документ имеет подчиненные - удалить невозможно.' });
          }
        }),
        catchError(this.handleError('deleteDocumentClick')),
        takeUntil(this.destroy$)
      ).subscribe();
  }

  /** Handles company-selection changes from the seller/buyer sub-component (stub — model update delegated to child). */
  onDocumentCompanyChanges(event) {
    console.log('*** EditDocumentComponent onDocumentCompanyChanges: ' /*+ JSON.stringify(event)*/);

  }

  /** Updates `document.DOCCLIENTSID` when the client selection changes. */
  onDocumentClientChanges(event) {
    console.log('*** EditDocumentComponent onDocumentClientChanges: '/* + JSON.stringify(event)*/);

    this.document.DOCCLIENTSID = event.DOCCLIENTSID;
  }

  /** Copies `event` text to the system clipboard via the Clipboard API; falls back silently on unsupported browsers. */
  copyClipBoard(event) {
    console.log('*** EditDocumentComponent copyClipBoard: ' + JSON.stringify(event));
    if (navigator.clipboard) {
      navigator.clipboard.writeText(event)
        .then(() => console.log('*** EditDocumentComponent copyClipBoard: ' + 'Text copied!'))
        .catch(err => console.error('Failed to copy text:', err));
    } else {
      // Fallback for older browsers (e.g., using document.execCommand('copy'))
      // This method is generally discouraged due to deprecation and limitations.
    }  }

  /** Replaces `document.items` with the imported rows and triggers a shallow document clone to notify child components. */
  onImportDone(event) {
    console.log('*** EditDocumentComponent onImportDone: ');

    this.document.items = event;

    this.document = { ... this.document };
  }

  /** Updates `documentContentStatus` and `document.DOCFLAG` from the content-validation result emitted by the child editor. */
  onGetDocumentStatus(event) {
    console.log('*** EditDocumentComponent onGetDocumentStatus: ' + JSON.stringify(event));

    this.documentContentStatus = event;
    this.applyDocFlag();

    // By fact, this UI behaivor appears very annoying, so it is disabled for now.

    // this.showTabErrors = this.document.DOCFLAG ? true : false;

  }

  /**
   * `DOCFLAG = max(editor flag, pre-flight severity)`, the pre-flight part counting only while its result is fresh.
   * Called whenever either input can have changed: a new row-status emission, a finished check, and just before a save.
   */
  private applyDocFlag(): void {
    if (!this.document) { return; }
    this.document.DOCFLAG = mergeDocFlag(this.documentContentStatus, preflightContribution(this.preflightCurrentState, this.preflightSignatureNow));
  }

  /** The signature of the document as it is now (lines, delivery date, company). */
  private get preflightSignatureNow(): string { return preflightSignature(this.document); }

  /** The held result, only while it belongs to the document on screen. */
  private get preflightCurrentState(): PreflightState | null {
    return this.preflightState && this.document && this.preflightState.result.documentId === this.document.DOCID
      ? this.preflightState : null;
  }

  /** The check exists for invoices only (owner decision Q1). */
  public get preflightApplicable(): boolean { return !!this._docId && !!this.document && this.document.DOCTYPE === 1; }

  /**
   * The button is enabled for a loaded invoice with no unsaved edits (Q17): the check reads the saved version.
   * A pending change of `DOCFLAG` alone does not count as an edit, otherwise running the check would disable the button.
   */
  public get preflightCanRun(): boolean {
    return this.preflightApplicable && !this.preflightLoading && !this.exportChecking && !this.editing && !this.contentLoading && this.headerEqual(['DOCFLAG']);
  }

  /** The held result for the document on screen, for the modal; null when none. */
  public get preflightResult() { return this.preflightCurrentState ? this.preflightCurrentState.result : null; }

  /** True when a result is held and the document has not changed since. */
  public get preflightFresh(): boolean { return preflightIsFresh(this.preflightCurrentState, this.preflightSignatureNow); }

  /** True when a result is held but the document was edited after it. */
  public get preflightStale(): boolean { return !!this.preflightCurrentState && !this.preflightFresh; }

  /** The badge on the toolbar button: "!" when stale (not re-verified), the severity of a fresh result with findings. */
  public get preflightBadge(): string {
    if (this.preflightStale) { return '!'; }
    const sev = this.preflightFresh ? this.preflightState.result.maxSeverity : 0;
    return sev > 0 ? String(sev) : '';
  }

  public get preflightBadgeClass(): string {
    if (this.preflightStale) { return 'p-badge-secondary'; }
    return this.preflightFresh && this.preflightState.result.maxSeverity >= 100 ? 'p-badge-danger' : 'p-badge-warning';
  }

  public get preflightTooltip(): string {
    if (this.preflightStale) { return 'Перевірка наявності: документ змінено після перевірки, не перевірено повторно'; }
    if (this.preflightFresh) {
      return this.preflightState.result.findings.length ? 'Перевірка наявності: є зауваження, натисніть, щоб переглянути' : 'Перевірка наявності: нестач не виявлено';
    }
    return this.preflightCanRun ? 'Перевірити наявність товару за документом' : 'Перевірка наявності: спершу збережіть зміни документа';
  }

  /**
   * Runs the pre-flight check on the saved invoice, keeps the result with the signature of the document as it was when
   * the request left (an edit made while it runs therefore shows as stale at once), merges it into `DOCFLAG`
   * and opens the modal. A failure shows a message in the modal and leaves the flag alone.
   */
  onPreflight(): void {
    if (!this.preflightCanRun) { return; }

    this.preflightLoading = true;
    this.preflightError = null;
    this.preflightDlg = true;

    this.requestPreflight()
      .pipe(finalize(() => this.preflightLoading = false))
      .subscribe({
        error: err => {
          environment.production || console.error('*** EditDocumentComponent onPreflight error:', err);
          this.preflightError = 'Не вдалося виконати перевірку наявності. Спробуйте ще раз.';
        }
      });
  }

  /**
   * One request of the pre-flight check, shared by the button and the "На печать" gate. Keeps the result with the
   * signature of the document as it was when the request left, and merges it into `DOCFLAG` when it arrives.
   */
  private requestPreflight(): Observable<PreflightState> {
    const settings = this.globalsettingsservice.getGlobalSettings();
    const signature = this.preflightSignatureNow;

    return this.preflightApi.getPreflight(
      this.document.DOCID,
      settings.mainStockId,
      settings.getImpStock(),
      settings.getAuxStock(),
      settings.manDepartmentId || 0
    )
      .pipe(
        map(result => ({ result, signature } as PreflightState)),
        tap(state => {
          this.preflightState = state;
          this.applyDocFlag();
        }),
        takeUntil(this.destroy$)
      );
  }

  /** Reflects the inline-editing state emitted by the child content component into `editing`. */
  onEditing(event) {
    console.log('*** EditDocumentComponent onEditing: ' + JSON.stringify(event));
    this.editing = event;
  }

  /** Reflects the "show empty rows" toggle state emitted by the child content component into `countEmpty`. */
  onCountEmptyChange(event) {
    console.log('*** EditDocumentComponent onCountEmptyChange: ' + JSON.stringify(event));
    this.countEmpty = event;
  }

  /** Syncs `contentLoading` with the loading state emitted by the child content component and sets `document.DOCUMENTISREADY` to the opposite value. */
  onLoadingContent(event) {
    console.log('*** EditDocumentComponent onLoadingContent: ' + JSON.stringify(event));
    this.contentLoading = event;
    this.document.DOCUMENTISREADY = !event;
  }

  /** Disables all navigation-menu items (called while a command is in progress to prevent concurrent navigation). */
  checkCommand(event) {
    console.log('*** EditDocumentComponent checkCommand: ' + JSON.stringify(event));
    this.navigationMenu.forEach(e => e.disabled = true);
  }

  /** Navigates to the `/documents` list page. */
  toHome() {
    console.log('*** EditDocumentComponent toHome: ');
    this.router.navigate(['/documents']);
  }
}

